import React, {useState, useCallback} from 'react';
import {ViewStyle, StyleProp} from 'react-native';
import {Box, Center, Text, Row, FormControl} from 'native-base';
import {FieldError, useController, UseControllerProps} from 'react-hook-form';
import {FileAttachmentIcon, FileUploadIcon} from './../../icons';

import {message as t} from '../../../utils/messages';

import {fileTypeList} from '../constants';
import {IFileForm} from '../../../interfaces/forms';
import Dropzone from 'react-dropzone';

/** The File interface provides information about files and allows JavaScript in a web page to access their content. */
interface File extends Blob {
  readonly lastModified: number;
  readonly name: string;
}

interface InputProps extends UseControllerProps<any> {
  label: string;
  info: {before: string; after: string};
  error: FieldError | undefined;
  style?: StyleProp<ViewStyle>;
}

const FileInput = (props: InputProps) => {
  const {label, error, control, name, rules, info, style} = props;
  const {field} = useController({control, defaultValue: '', name, rules});

  const [fileResponse, setFileResponse] = useState([] as any[] | File[]);

  const parseFilePicked = (asset: File): IFileForm | undefined => {
    return {
      name: asset.name ?? '',
      size: asset.size ?? 0,
      uri: '',
      type: asset.type ?? '',
      content: undefined,
    };
  };

  const handlerOnDrop = useCallback(
    (acceptedFiles: File[]) => {
      setFileResponse(acceptedFiles);
      acceptedFiles.forEach(file => {
        const reader = new FileReader();
        reader.onabort = () => console.debug('file reading was aborted');
        reader.onerror = () => console.debug('file reading has failed');
        reader.onload = () => {
          // Do whatever you want with the file contents
          const contentBase64 = reader.result;
          const result = parseFilePicked(file);
          if (result && result.size > 0) {
            result.content = contentBase64 as string;
            field.onChange(result);
          } else {
            throw new Error('Fail photo selection');
          }
        };
        reader.readAsDataURL(file);
      });
    },
    [field],
  );

  const getErrorMessage = () => {
    return error != null ? t(error.message as string) : '';
  };

  return (
    <>
      <FormControl isRequired isInvalid={error != null} {...{style}}>
        <FormControl.Label _text={{color: '#06355E', fontWeight: 'bold'}}>
          {t(label)}
        </FormControl.Label>
        <Dropzone useFsAccessApi={true} onDrop={handlerOnDrop}>
          {({getRootProps, getInputProps}) => (
            <div {...getRootProps()}>
              <input {...getInputProps()} />
              <Box
                borderColor={error != null ? 'red.500' : 'blue.400'}
                borderWidth="1"
                borderRadius="10"
                paddingY="5">
                <Center>
                  {fileResponse.length > 0 ? (
                    <FileAttachmentIcon
                      color="blue.400"
                      width="20"
                      height="20"
                    />
                  ) : (
                    <FileUploadIcon color="blue.400" width="20" height="20" />
                  )}
                  <Text color="blue.400" fontSize="md" fontWeight="bold">
                    {t(fileResponse.length > 0 ? info.before : info.after)}
                  </Text>
                </Center>
              </Box>
            </div>
          )}
        </Dropzone>

        {fileTypeList.length > 0 && (
          <Row mt="2">
            <Text fontSize="xs" color="gray.400">
              {t('input-file-label-support')}
            </Text>
            {fileTypeList.map(item => (
              <Text
                key={item.label}
                fontSize="xs"
                color={item.color}
                marginLeft="2">
                {item.label}
              </Text>
            ))}
          </Row>
        )}
        <FormControl.ErrorMessage mt="0">
          {getErrorMessage()}
        </FormControl.ErrorMessage>
      </FormControl>
    </>
  );
};

export default FileInput;
